import { area as turfArea } from "@turf/area";
import { centerOfMass } from "@turf/center-of-mass";
import { distance as turfDistance } from "@turf/distance";
import { lineString } from "@turf/helpers";
import { length as turfLength } from "@turf/length";
import { lineToPolygon } from "@turf/line-to-polygon";
import type { Position } from "geojson";

import IMDFFeature from "@/libs/imdf/feature";
import type IMDFTheme from "@/libs/imdf/theme";
import type { LatLng } from "@/libs/models";
import { addZCoordinate, getSquareCoordinates } from "../geo";
import type IMDF from "./imdf";

const MIN_ICON_HEIGHT = 0.15;

export const featureTypesToGetOpenings = ["opening"];
export const geometriesToExtrude = ["Polygon", "MultiPolygon"];
export const categoriesToNotScale = ["furniture", "escalator", "fixture"];

/**
 * Given a list of features and a margin, returns a list of features representing
 * the "squared" version of the openings.
 * The squared version of an opening is a polygon whose coordinates are the
 * same as the coordinates of the original opening, but with the last coordinate
 * repeated to close the polygon.
 *
 * @param {IMDFFeature[]} features List of features
 * @param {number} margin The margin to use when calculating the squared version of the openings
 *
 * @returns {IMDFFeature[]} The list of squared openings
 */
export function getOpeningsSquared(
  features: IMDFFeature[],
  margin: number,
): IMDFFeature[] {
  return features
    .filter((element: IMDFFeature) =>
      featureTypesToGetOpenings.includes(element.properties.feature_type),
    )
    .map((element: IMDFFeature) => {
      const squareCoordinates = getSquareCoordinates(
        element.geometry.coordinates[0],
        element.geometry.coordinates[1],
        margin * 2,
      );

      return new IMDFFeature({
        feature_type: element.feature_type,
        geometry: lineToPolygon(lineString(squareCoordinates)).geometry,
        id: element.id,
        properties: element.properties,
        type: element.type,
      });
    });
}

// Utility function to check if scaling is required
const shouldScale = (element: IMDFFeature): boolean => {
  return !categoriesToNotScale.includes(element.properties.category);
};

/**
 * Given a list of features and a theme, returns the features that should be
 * extruded according to the theme.
 *
 * This function is used to filter the features that should be extruded before
 * calculating the walls of a building.
 *
 * @param features List of features
 * @param theme Theme
 * @returns List of features that should be extruded
 */
export function getFeaturesToExtrude(
  features: IMDFFeature[],
  theme: IMDFTheme,
) {
  return features?.filter((feature: IMDFFeature) =>
    feature.shouldExtrude(theme),
  );
}

/**
 * Given a list of features and a polygon margin, returns a list of features that
 * have been scaled by the given margin.
 *
 * This function is used to scale walls before calculating the walls of a building.
 * It is done by scaling all the features that should be extruded (i.e. excluding
 * features that should not be extruded like furniture, escalators, fixtures, etc.)
 *
 * @param features List of features
 * @param polygonMargin Polygon margin
 * @returns List of scaled features
 */
export function scaleFeaturesThatShouldBeScaled(
  features: IMDFFeature[],
  polygonMargin: number,
) {
  const featuresToScale = features?.filter(shouldScale) || [];
  return featuresToScale.map((feature: IMDFFeature) => {
    return feature.scale(polygonMargin);
  });
}

/**
 * Finds the best match for each feature in the target IMDF in the origin IMDF
 * based on geometric characteristics of the features.
 *
 * @param targetIMDF The IMDF to find matches for
 * @param originIMDF The IMDF to find matches in
 * @param maxDistance The maximum distance between two features for them to be considered a match
 * @param [areaWeight=1/3] The weight of the area similarity in the match score
 * @param [compactnessWeight=1/3] The weight of the compactness similarity in the match score
 * @param [maxDissimilarity=0.5] The maximum dissimilarity between two features for them to be considered a match
 * @returns An array of arrays, where each inner array contains the IDs of two features that are a match
 */
export function matchFeatures(
  targetIMDF: IMDF,
  originIMDF: IMDF,
  maxDistance: number,
  areaWeight: number = 1 / 3, // This parameter can be adjusted to emphasize area similarity more or less
  compactnessWeight: number = 1 / 3,
  maxDissimilarity: number = 0.5,
) {
  interface FeatureDescriptors {
    id: string;
    centroid: Position;
    area: number;
    compactness: number;
  }

  // Function to compute metrics for a list of features
  function computeFeatureDescriptors(
    features: IMDFFeature[],
  ): FeatureDescriptors[] {
    return features
      .map((feature) => {
        if (
          feature.id &&
          feature.geometry.type !== "Point" &&
          feature.geometry &&
          feature.geometry.coordinates.length > 0
        ) {
          const centroid = centerOfMass(feature.geometry).geometry.coordinates;
          const area = turfArea(feature.geometry);
          const perimeter = turfLength(feature.toTurfLine(), {
            units: "meters",
          });

          return {
            area,
            centroid,
            compactness: (perimeter * perimeter) / area,
            id: feature.id,
          };
        }
        return null;
      })
      .filter((feature): feature is FeatureDescriptors => feature !== null);
  }

  // Calculates the match score based on geometric characteristics of two features
  function computeDissimilarity(
    featureDescriptorA: FeatureDescriptors,
    featureDescriptorB: FeatureDescriptors,
    maxDistance: number,
    areaWeight: number,
    compactnessWeight: number,
  ) {
    //Compute dissimilarities
    const distance = turfDistance(
      featureDescriptorA.centroid,
      featureDescriptorB.centroid,
      { units: "meters" },
    );
    if (distance > maxDistance) return Infinity; // Early exit if features are too far apart
    const areaDiff = Math.abs(
      featureDescriptorA.area - featureDescriptorB.area,
    );
    const compactnessDiff = Math.abs(
      featureDescriptorA.compactness - featureDescriptorB.compactness,
    );

    //Normalize dissimilarities
    const normalizedDistance = distance / maxDistance;
    const normalizedAreaDiff =
      areaDiff / (featureDescriptorA.area + featureDescriptorB.area);
    const normalizedCompactnessDiff =
      compactnessDiff /
      (featureDescriptorA.compactness + featureDescriptorB.compactness);

    //Combine dissimilarities
    return (
      normalizedDistance * (1 - areaWeight - compactnessWeight) +
      normalizedAreaDiff * areaWeight +
      normalizedCompactnessDiff * compactnessWeight
    );
  }

  const matches: string[][] = [];

  targetIMDF.getLevelOrdinals().forEach((floorLevel: number) => {
    const targetDescriptors = computeFeatureDescriptors(
      targetIMDF.getFeaturesByFloorLevel(floorLevel),
    );
    const originDescriptors = computeFeatureDescriptors(
      originIMDF.getFeaturesByFloorLevel(floorLevel),
    );

    targetDescriptors.forEach((targetDescriptor: FeatureDescriptors) => {
      let bestMatchId = null;
      let minDissimilarity = Infinity;

      originDescriptors.forEach((originDescriptor: FeatureDescriptors) => {
        const dissimilarity = computeDissimilarity(
          targetDescriptor,
          originDescriptor,
          maxDistance,
          areaWeight,
          compactnessWeight,
        );

        if (dissimilarity < minDissimilarity) {
          minDissimilarity = dissimilarity;
          bestMatchId = originDescriptor.id;
        }
      });

      if (bestMatchId !== null && minDissimilarity < maxDissimilarity) {
        matches.push([targetDescriptor.id, bestMatchId]);
      }
    });
  });

  return matches;
}

export const getVisibleFeatures = (features: IMDFFeature[]) => {
  return features.filter(
    (f: IMDFFeature) => !("show" in f.properties && f.properties.show == false),
  );
};

export const scaleFeatures = (
  features: IMDFFeature[],
  polygonMargin: number,
  theme: IMDFTheme,
) => {
  return features.map((feature: IMDFFeature) => {
    return feature.scale(-polygonMargin - theme.getMargin(feature));
  });
};

export const getExtrudedFeatures = (
  features: IMDFFeature[],
  theme: IMDFTheme,
  extrudePolygons: boolean,
) => {
  return features.filter((f: IMDFFeature) => {
    return (
      f.geometry.type != "Point" &&
      theme.getExtrusionHeight(f) > 0 &&
      extrudePolygons
    );
  });
};

export const getNonExtrudedFeatures = (
  features: IMDFFeature[],
  theme: IMDFTheme,
  extrudePolygons: boolean,
) => {
  return features
    .filter((f: IMDFFeature) => {
      return (
        f.geometry.type != "Point" &&
        (theme.getExtrusionHeight(f) == 0 || !extrudePolygons)
      );
    })
    .map((f) => {
      if (!f.geometry?.coordinates) return f;
      const newFeature = new IMDFFeature({ ...f });
      newFeature.setCoordinates(
        addZCoordinate(f.getCoordinates(), f.getSafeZ()),
      );
      return newFeature;
    });
};

export const getPointFeatures = (features: IMDFFeature[]) => {
  return features
    .filter((f) => f.geometry && f.geometry?.type == "Point")
    .map((feature) => {
      feature.setCoordinates([
        feature.geometry.coordinates[0] as number,
        feature.geometry.coordinates[1] as number,
        (feature.geometry.coordinates[2] as number) ?? MIN_ICON_HEIGHT,
      ]);
      return feature;
    });
};

export const coordsToFeature = (coordinates: LatLng[]) => {
  const polygonCoords = coordinates.reduce((coords, c) => {
    coords.push([c.lng, c.lat, 0]);
    return coords;
  }, []);

  // Close polygon
  if (coordinates.length > 0) {
    polygonCoords.push([coordinates[0].lng, coordinates[0].lat, 0]);
  }
  return IMDFFeature.fromCoords(polygonCoords);
};
